import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { useSubscription } from '../context/SubscriptionContext';
import { useToast } from '../context/ToastContext';
import UpgradeModal from './UpgradeModal';
import { dvlaLookup, motWebsiteLookup } from '../services/firebaseFunctions';
import { todayDateOnly, normalizeMotTimeSlot, formatVehicleDisplayName, MOT_SLOT_DURATIONS, DEFAULT_MOT_SLOT_DURATION, generateMotTimeSlots, isMotTimeSlotUnavailable } from '../utils/helpers';
import { getDefaultBookingStage, getEnabledStages, getStageSelectOptions } from '../utils/enabledStages';
import { sendMotBookedConfirmationWhatsApp } from '../services/whatsappService';
import { currentCompanyName } from '../firebase/config';

const toSentenceCaps = (s) => {
  if (!s) return '';
  return String(s).replace(/(^\s*|[.!?]\s+)([a-z])/g, (match, prefix, letter) => prefix + letter.toUpperCase());
};

const BookingModal = ({ onClose }) => {
  const { addVehicle, createInvoiceForVehicle, vehicles, garageInfo, updateGarageInfo } = useApp();
  const { canAddVehicle } = useSubscription();
  const toast = useToast();
  const regInputRef = useRef(null);
  const formRef = useRef(null);

  // Keyboard shortcut Ctrl+S: submit form when modal is open
  useEffect(() => {
    const handler = () => formRef.current?.requestSubmit();
    window.addEventListener('app-save', handler);
    return () => window.removeEventListener('app-save', handler);
  }, []);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const [formData, setFormData] = useState({
    reg: '',
    vehicleName: '',
    motStatus: '',
    taxStatus: '',
    nextServiceDue: '',
    mileage: '',
    technician: '',
    customerName: '',
    contact: '',
    email: '',
    address: '',
    postcode: '',
    city: '',
    keyCode: '',
    cost: '',
    status: getDefaultBookingStage(garageInfo),
    dropOffDate: todayDateOnly(),
    collectionDate: '',
    summary: '',
    partsOnOrder: false,
    paid: false,
    collected: false,
    // MOT time slot
    timeSlot: '',
    motSlotDuration: DEFAULT_MOT_SLOT_DURATION,
    // Sales-specific fields
    prepCost: '',
    warranty: '',
    misc: '',
    salePrice: '',
    dealStage: 'inStock',
    // Vehicle documentation fields
    v5c: '',
    keys: '',
    import: '',
    serviceHistory: '',
    motExpiryDate: null, // ISO string from DVLA, used for "MOT expiring soon" check
    // Tyres-specific fields
    tyreSize: '',
    tyreQuantity: '',
    tyrePosition: '',
    tyreServiceType: '',
    // Tyre Tread Depths (FL, FR, RL, RR, SP)
    treadFL: '', treadFR: '', treadRL: '', treadRR: '', treadSP: '',
    // Tyre Size, Make & Rate
    tyreSizeFront: '', tyreSizeRear: '', tyreRate: '', tyreMakeFront: '', tyreMakeRear: '', loadIndex: '',
    // Tyre Pressures / Wheel Torque (FL, FR, RL, RR, SP, NM)
    pressureFL: '', pressureFR: '', pressureRL: '', pressureRR: '', pressureSP: '', wheelTorqueNM: ''
  });

  const [dvlaStatus, setDvlaStatus] = useState({
    message: 'Enter registration and click Lookup DVLA to fetch vehicle details.',
    type: 'default' // default, success, error
  });
  const [isLookingUp, setIsLookingUp] = useState(false);
  // Technicians come from garageInfo (company settings), not users
  const technicians = [...(garageInfo?.technicians || [])].sort((a, b) =>
    (a || '').toLowerCase().localeCompare((b || '').toLowerCase())
  );
  const [showAddTechnician, setShowAddTechnician] = useState(false);
  const [newTechnicianName, setNewTechnicianName] = useState('');
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    // Focus on registration input when modal opens
    if (regInputRef.current) {
      regInputRef.current.focus();
    }

    // Pre-fill default technician (and site if we had a field) from Settings
    if (garageInfo?.defaultTechnician) {
      setFormData(prev => ({ ...prev, technician: garageInfo.defaultTechnician }));
    }

    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
    };
  }, [garageInfo?.defaultTechnician]);

  useEffect(() => {
    const enabled = getEnabledStages(garageInfo);
    const fallback = getDefaultBookingStage(garageInfo);
    setFormData((prev) => (enabled.includes(prev.status) ? prev : { ...prev, status: fallback }));
  }, [garageInfo]);

  const handleClose = () => {
    // Reset form
    setFormData({
      reg: '',
      vehicleName: '',
      motStatus: '',
      taxStatus: '',
      nextServiceDue: '',
      mileage: '',
      technician: '',
      customerName: '',
      contact: '',
      email: '',
      address: '',
      postcode: '',
      city: '',
      keyCode: '',
      cost: '',
      status: getDefaultBookingStage(garageInfo),
      dropOffDate: todayDateOnly(),
      collectionDate: '',
      summary: '',
      partsOnOrder: false,
      paid: false,
      collected: false,
      // MOT time slot
      timeSlot: '',
      motSlotDuration: DEFAULT_MOT_SLOT_DURATION,
      // Sales-specific fields
      prepCost: '',
      warranty: '',
      misc: '',
      salePrice: '',
      dealStage: 'inStock',
      // Vehicle documentation fields
      v5c: '',
      keys: '',
      import: '',
      serviceHistory: '',
      motExpiryDate: null,
      tyreSize: '',
      tyreQuantity: '',
      tyrePosition: '',
      tyreServiceType: '',
      treadFL: '', treadFR: '', treadRL: '', treadRR: '', treadSP: '',
      tyreSizeFront: '', tyreSizeRear: '', tyreRate: '', tyreMakeFront: '', tyreMakeRear: '', loadIndex: '',
      pressureFL: '', pressureFR: '', pressureRL: '', pressureRR: '', pressureSP: '', wheelTorqueNM: ''
    });
    setDvlaStatus({
      message: 'Enter registration and click Lookup DVLA to fetch vehicle details.',
      type: 'default'
    });
    setIsLookingUp(false);
    setShowAddTechnician(false);
    setNewTechnicianName('');
    setEmailError('');
    onClose();
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    // For contact field, only allow digits
    const sanitizedValue = name === 'contact' ? value.replace(/\D/g, '') : value;
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: type === 'checkbox' ? checked : sanitizedValue
      };
      // Reset sales-specific fields when status changes away from Sales
      if (name === 'status' && value !== 'Sales') {
        updated.prepCost = '';
        updated.warranty = '';
        updated.misc = '';
        updated.salePrice = '';
        updated.dealStage = 'inStock';
      }
      // Reset timeSlot when status changes away from MOT
      if (name === 'status' && value !== 'MOT') {
        updated.timeSlot = '';
        updated.motSlotDuration = DEFAULT_MOT_SLOT_DURATION;
      }
      // Reset Tyres-specific fields when status changes away from Tyres
      if (name === 'status' && value !== 'Tyres') {
        updated.tyreSize = '';
        updated.tyreQuantity = '';
        updated.tyrePosition = '';
        updated.tyreServiceType = '';
        updated.treadFL = ''; updated.treadFR = ''; updated.treadRL = ''; updated.treadRR = ''; updated.treadSP = '';
        updated.tyreSizeFront = ''; updated.tyreSizeRear = ''; updated.tyreRate = ''; updated.tyreMakeFront = ''; updated.tyreMakeRear = ''; updated.loadIndex = '';
        updated.pressureFL = ''; updated.pressureFR = ''; updated.pressureRL = ''; updated.pressureRR = ''; updated.pressureSP = ''; updated.wheelTorqueNM = '';
      }
      if (name === 'status' && value === 'Tyres') {
        updated.tyreServiceType = 'Supply and fit';
      }
      // Clear motExpiryDate when user edits MOT status manually
      if (name === 'motStatus') {
        updated.motExpiryDate = null;
      }
      return updated;
    });
    if (name === 'email') setEmailError('');
    // Registration: no auto-lookup; user must click "Lookup DVLA" to trigger lookup.
  };

  const handleDvlaLookup = async (registration = null) => {
    const reg = registration || formData.reg.trim().toUpperCase().replace(/\s+/g, '');
    if (!reg || reg.length < 4) {
      setDvlaStatus({
        message: 'Enter a valid registration (at least 4 characters).',
        type: 'error'
      });
      return;
    }

    // Prevent multiple simultaneous lookups
    if (isLookingUp) {
      return;
    }

    setIsLookingUp(true);
    setDvlaStatus({
      message: 'Looking up vehicle...',
      type: 'default'
    });

    try {
      // DVLA for tax/MOT/year; MOT website for make+model (parallel)
      const [dvlaResult, motWebsiteResult] = await Promise.allSettled([
        dvlaLookup(reg),
        motWebsiteLookup(reg),
      ]);

      if (dvlaResult.status !== 'fulfilled') {
        throw dvlaResult.reason;
      }
      const data = dvlaResult.value;
      
      // Handle error response
      if (data.error) {
        const error = new Error(data.error);
        error.statusCode = data.statusCode || 400;
        error.details = data.details;
        throw error;
      }
      
      // Validate that we have at least make or model
      if (!data.make && !data.model) {
        const error = new Error('No vehicle data found. The registration may be invalid or not in the DVLA database.');
        error.statusCode = 404;
        throw error;
      }
      
      // Extract vehicle details from DVLA API response
      // Note: DVLA API uses 'engineCapacity' not 'cylinderCapacity'
      let make = data.make || '';
      let model = data.model || '';
      const cylinderCapacity = data.cylinderCapacity || data.engineCapacity || null;
      const fuelType = data.fuelType || '';
      const yearOfManufacture = data.yearOfManufacture || null;

      // Prefer make+model from MOT website (DVLA usually has no model)
      const scraped =
        motWebsiteResult.status === 'fulfilled' ? motWebsiteResult.value : null;
      if (scraped?.make) make = scraped.make;
      if (scraped?.model) model = scraped.model;

      // Friendly name only — never append engine/fuel to Make & Model
      const vehicleName = formatVehicleDisplayName({
        make,
        model,
        year: yearOfManufacture,
      });
      
      // Engine info for status toast only
      const engineParts = [];
      if (cylinderCapacity) {
        // Convert cc to litres if >= 1000cc, otherwise show in cc
        const engineSize = cylinderCapacity >= 1000 
          ? `${(cylinderCapacity / 1000).toFixed(1)}L` 
          : `${cylinderCapacity}cc`;
        engineParts.push(engineSize);
      }
      if (fuelType) {
        engineParts.push(fuelType);
      }
      
      // Format MOT expiry date if available
      let motStatus = '';
      if (data.motExpiryDate) {
        const motDate = new Date(data.motExpiryDate);
        const today = new Date();
        if (motDate > today) {
          motStatus = `MOT valid until ${motDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
        } else {
          motStatus = `MOT expired on ${motDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
        }
      } else if (data.motStatus) {
        motStatus = data.motStatus;
      } else {
        motStatus = 'MOT status not available';
      }

      // Format tax status if available
      let taxStatus = '';
      if (data.taxStatus) {
        taxStatus = data.taxStatus;
        if (data.taxDueDate) {
          const taxDate = new Date(data.taxDueDate);
          const today = new Date();
          if (taxStatus.toLowerCase() === 'taxed' || taxStatus.toLowerCase() === 'sorn') {
            if (taxDate > today) {
              taxStatus = `${taxStatus} - Due ${taxDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
            } else {
              taxStatus = `${taxStatus} - Expired ${taxDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
            }
          }
        }
      } else if (data.taxed !== undefined) {
        taxStatus = data.taxed ? 'Taxed' : 'Untaxed';
        if (data.taxDueDate) {
          const taxDate = new Date(data.taxDueDate);
          const today = new Date();
          if (data.taxed && taxDate > today) {
            taxStatus += ` - Due ${taxDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
          }
        }
      } else {
        taxStatus = 'Tax status not available';
      }

      setFormData(prev => ({
        ...prev,
        vehicleName: prev.vehicleName || vehicleName,
        motStatus: motStatus,
        taxStatus: taxStatus,
        motExpiryDate: data.motExpiryDate || null
      }));

      // Build success message with vehicle details
      let successMessage = 'Vehicle details successfully retrieved from DVLA.';
      if (make) {
        successMessage = `Retrieved: ${formatVehicleDisplayName({ make, model, year: yearOfManufacture }) || make}`;
        if (engineParts.length > 0) {
          successMessage += ` — ${engineParts.join(' ')}`;
        }
      }
      
      setDvlaStatus({
        message: successMessage,
        type: 'success'
      });
    } catch (error) {
      console.error('DVLA API Error:', error);
      let errorMessage = 'Failed to retrieve vehicle details from DVLA.';
      
      // Check for network errors (Firebase function not accessible)
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError') || error.name === 'TypeError') {
        errorMessage = 'Cannot connect to DVLA service. Please check that the Firebase function is deployed and has public access permissions.';
      } else if (error.statusCode === 404 || error.message.includes('not found') || error.message.includes('No vehicle data')) {
        errorMessage = 'Registration not found in DVLA database. Please check the registration number is correct and try again.';
        if (error.details) {
          errorMessage += ` (${error.details})`;
        }
      } else if (error.statusCode === 400 || error.message.includes('Invalid registration') || error.message.includes('Bad Request')) {
        errorMessage = 'Invalid registration number format. Please check the registration and try again.';
        if (error.details) {
          errorMessage += ` (${error.details})`;
        }
      } else if (error.statusCode === 401 || error.statusCode === 403 || error.message.includes('authentication failed') || error.message.includes('Invalid API key')) {
        errorMessage = 'DVLA API authentication failed. Please check the API key configuration in the server.';
        if (error.details) {
          errorMessage += ` (${error.details})`;
        }
      } else if (error.statusCode === 429 || error.message.includes('Too many requests')) {
        errorMessage = 'Too many requests. Please wait a moment and try again.';
      } else if (error.message) {
        errorMessage = error.message;
        if (error.details) {
          errorMessage += ` (${error.details})`;
        }
      }

      setDvlaStatus({
        message: errorMessage,
        type: 'error'
      });
    } finally {
      setIsLookingUp(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const reg = formData.reg.trim().toUpperCase();
    if (!reg) {
      setDvlaStatus({
        message: 'Registration is required.',
        type: 'error'
      });
      return;
    }

    // Validate email if provided
    const email = (formData.email || '').trim();
    if (email && !email.includes('@')) {
      setActiveTab(1);
      setEmailError('Please enter a valid email address.');
      return;
    }

    // Validate time slot is selected for MOT bookings
    if (formData.status === 'MOT' && !formData.timeSlot) {
      setDvlaStatus({
        message: 'Please select a time slot for this MOT booking.',
        type: 'error'
      });
      return;
    }

    // Check if selected time slot is already booked (double-check before submission)
    if (formData.status === 'MOT' && formData.timeSlot) {
      const slotKey = formData.timeSlot.includes('T') 
        ? formData.timeSlot 
        : `${formData.dropOffDate}T${formData.timeSlot}`;
      
      if (isMotTimeSlotUnavailable(
        slotKey,
        formData.dropOffDate,
        formData.motSlotDuration || DEFAULT_MOT_SLOT_DURATION,
        vehicles,
      )) {
        setDvlaStatus({
          message: 'This time slot overlaps another MOT booking. Please select another time or duration.',
          type: 'error'
        });
        return;
      }
    }

    // Check vehicle limit before adding
    const currentVehicleCount = vehicles.length;
    if (!canAddVehicle(currentVehicleCount)) {
      setShowUpgradeModal(true);
      return;
    }

    const newVehicle = {
      reg,
      vehicle: formData.vehicleName.trim(),
      status: formData.status,
      summary: toSentenceCaps(formData.summary.trim()),
      technician: formData.status === 'Sales' ? '' : formData.technician.trim(),
      customerName: formData.customerName.trim(),
      contact: formData.contact.trim(),
      email: formData.email.trim(),
      address: formData.address.trim(),
      postcode: formData.postcode.trim(),
      city: formData.city.trim(),
      keyCode: formData.keyCode.trim(),
      cost: parseFloat(formData.cost || '0'),
      partsOnOrder: formData.partsOnOrder,
      notes: '',
      motStatus: (formData.motStatus || '').trim() || (formData.status === 'Sales' ? '' : 'MOT status not recorded'),
      taxStatus: (formData.taxStatus || '').trim(),
      nextServiceDue: formData.nextServiceDue || null,
      mileage: (formData.mileage || '').trim().replace(/,/g, '') || null,
      paid: formData.paid,
      collected: formData.collected,
      dropOffDate: formData.dropOffDate,
      receivedDate: formData.dropOffDate,
      collectionDate: formData.collectionDate || null,
      timeSlot: formData.status === 'MOT' ? normalizeMotTimeSlot(formData.timeSlot, formData.dropOffDate) : '',
      motSlotDuration: formData.status === 'MOT'
        ? (MOT_SLOT_DURATIONS.includes(Number(formData.motSlotDuration))
          ? Number(formData.motSlotDuration)
          : DEFAULT_MOT_SLOT_DURATION)
        : null,
      location: '',
      // Store estimated cost for MOT/Workshop/Bodyshop/Tyres to show on invoice
      ...(['MOT', 'Workshop', 'Bodyshop', 'Tyres'].includes(formData.status) && {
        estimatedCost: parseFloat(formData.cost || '0')
      }),
      // Tyres-specific fields
      ...(formData.status === 'Tyres' && {
        tyreSize: (formData.tyreSize || '').trim(),
        tyreQuantity: (formData.tyreQuantity || '').trim(),
        tyrePosition: (formData.tyrePosition || '').trim(),
        tyreServiceType: (formData.tyreServiceType || 'Supply and fit').trim(),
        treadFL: (formData.treadFL || '').trim(), treadFR: (formData.treadFR || '').trim(),
        treadRL: (formData.treadRL || '').trim(), treadRR: (formData.treadRR || '').trim(),
        treadSP: (formData.treadSP || '').trim(),
        tyreSizeFront: (formData.tyreSizeFront || '').trim(), tyreSizeRear: (formData.tyreSizeRear || '').trim(),
        tyreRate: (formData.tyreRate || '').trim(), tyreMakeFront: (formData.tyreMakeFront || '').trim(),
        tyreMakeRear: (formData.tyreMakeRear || '').trim(), loadIndex: (formData.loadIndex || '').trim(),
        pressureFL: (formData.pressureFL || '').trim(), pressureFR: (formData.pressureFR || '').trim(),
        pressureRL: (formData.pressureRL || '').trim(), pressureRR: (formData.pressureRR || '').trim(),
        pressureSP: (formData.pressureSP || '').trim(), wheelTorqueNM: (formData.wheelTorqueNM || '').trim()
      }),
      // Sales-specific fields
      prepCost: formData.status === 'Sales' ? parseFloat(formData.prepCost || '0') : 0,
      warranty: formData.status === 'Sales' ? parseFloat(formData.warranty || '0') : 0,
      misc: formData.status === 'Sales' ? parseFloat(formData.misc || '0') : 0,
      salePrice: formData.status === 'Sales' ? parseFloat(formData.salePrice || '0') : 0,
      dealStage: formData.status === 'Sales' ? formData.dealStage : 'inStock',
      // Vehicle documentation fields
      v5c: formData.v5c || '',
      keys: formData.keys || '',
      import: formData.import || '',
      serviceHistory: formData.serviceHistory || ''
    };

    // Add vehicle to the beginning of the list
    addVehicle(newVehicle);

    // Automatically create invoice if vehicle is marked as paid
    if (newVehicle.paid) {
      createInvoiceForVehicle(newVehicle);
    }

    if (newVehicle.status === 'MOT') {
      sendMotBookedConfirmationWhatsApp({
        contact: newVehicle.contact,
        customerName: newVehicle.customerName,
        companyName: garageInfo?.companyName || 'Garage',
        vehicleReg: newVehicle.reg,
        vehicleName: newVehicle.vehicle,
        timeSlot: newVehicle.timeSlot,
        dropOffDate: newVehicle.dropOffDate,
        companyId: currentCompanyName,
      })
        .then((result) => {
          if (result.sent) {
            toast.success('MOT confirmation sent on WhatsApp');
          } else if (result.skipped === 'no_phone') {
            toast.info('MOT booked. Add a mobile number to send WhatsApp confirmation.');
          }
        })
        .catch((err) => {
          toast.error(err?.message || 'MOT booked, but WhatsApp confirmation failed.');
        });
    }

    handleClose();
  };

  const handleUpgradeToPayment = () => {
    setShowUpgradeModal(false);
    handleClose();
    // Navigate to payment page
    window.location.hash = '#payment';
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  };

  const getDvlaStatusClass = () => {
    switch (dvlaStatus.type) {
      case 'success':
        return 'dvla-status success';
      case 'error':
        return 'dvla-status error';
      default:
        return 'dvla-status';
    }
  };

  return (
    <div 
      className="modal-backdrop" 
      style={{ display: 'flex' }}
    >
      <div className="modal booking-modal" onClick={(e) => e.stopPropagation()}>
        <button 
          type="button" 
          className="modal-close" 
          onClick={handleClose}
          aria-label="Close modal"
        >
          ×
        </button>
        <h3 style={{ flexShrink: 0, marginBottom: '16px' }}>Add Vehicle Booking</h3>
        
        {/* Custom tabs for organizing form sections */}
        <div className="booking-modal-tabs" style={{
          display: 'flex',
          borderBottom: '2px solid var(--border)',
          marginBottom: '20px',
          gap: '8px'
        }}>
          <button
            type="button"
            onClick={() => setActiveTab(0)}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: 'none',
              borderBottom: activeTab === 0 ? '3px solid var(--primary)' : '3px solid transparent',
              background: 'transparent',
              color: activeTab === 0 ? 'var(--primary)' : 'var(--text-muted)',
              fontWeight: activeTab === 0 ? '600' : '500',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              marginBottom: '-2px'
            }}
          >
            Vehicle Details
          </button>
          <button
            type="button"
            onClick={() => setActiveTab(1)}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: 'none',
              borderBottom: activeTab === 1 ? '3px solid var(--primary)' : '3px solid transparent',
              background: 'transparent',
              color: activeTab === 1 ? 'var(--primary)' : 'var(--text-muted)',
              fontWeight: activeTab === 1 ? '600' : '500',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              marginBottom: '-2px'
            }}
          >
            Customer Details
          </button>
        </div>

        <form ref={formRef} className="modal-form" id="newVehicleForm" onSubmit={handleSubmit} style={{ flex: 1, minHeight: 0 }}>
          {/* Vehicle Details Tab */}
          {activeTab === 0 && (
            <>
              {dvlaStatus.type === 'error' && (
                <div className={getDvlaStatusClass()} id="dvlaStatus" style={{ marginBottom: '16px' }}>
                  {dvlaStatus.message}
                </div>
              )}
              <div className="form-row">
            <label htmlFor="newStatus">Stage</label>
            <select 
              id="newStatus" 
              name="status"
              required
              value={formData.status}
              onChange={handleInputChange}
              style={{
                backgroundColor: 'rgba(79, 141, 243, 0.1)',
                borderColor: 'rgba(79, 141, 243, 0.3)'
              }}
            >
              {getStageSelectOptions(garageInfo, formData.status).map((stage) => (
                <option key={stage} value={stage}>{stage}</option>
              ))}
            </select>
          </div>

          <div className="form-row double">
            <div className="form-row">
              <label htmlFor="newReg">Registration</label>
              <div className="input-group">
                <input 
                  id="newReg" 
                  ref={regInputRef}
                  name="reg"
                  type="text" 
                  required
                  value={formData.reg}
                  onChange={handleInputChange}
                  placeholder="Enter registration"
                  disabled={isLookingUp}
                />
                <button 
                  type="button" 
                  className="secondary-btn" 
                  id="dvlaLookup"
                  onClick={() => handleDvlaLookup()}
                  disabled={isLookingUp || !formData.reg.trim()}
                >
                  {isLookingUp ? 'Looking up...' : 'Lookup DVLA'}
                </button>
              </div>
              {dvlaStatus.type !== 'error' && (
                <div className={getDvlaStatusClass()} id="dvlaStatus">
                  {dvlaStatus.message}
                </div>
              )}
            </div>
            {(() => {
              let motAlert = null;
              let motInputStyle = {};
              if (formData.motExpiryDate) {
                const expiryDate = new Date(formData.motExpiryDate);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
                if (daysUntilExpiry > 0 && daysUntilExpiry <= 30) {
                  motAlert = { message: 'MOT expiring soon', color: '#F59E0B' };
                  motInputStyle = { borderColor: '#F59E0B', backgroundColor: 'rgba(245, 158, 11, 0.08)' };
                } else if (daysUntilExpiry <= 0) {
                  motAlert = { message: 'MOT expired', color: '#EF4444' };
                  motInputStyle = { borderColor: '#EF4444', backgroundColor: 'rgba(239, 68, 68, 0.08)' };
                }
              }
              const sharedInputStyle = { width: '100%', minWidth: 0, boxSizing: 'border-box', height: '44px' };
              return (
              <div className="form-row" style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', minWidth: '290px' }}>
                <div style={{ width: '100%', minWidth: 0 }}>
                  <label htmlFor="newMotStatus">MOT Status</label>
                  <input 
                    id="newMotStatus" 
                    name="motStatus"
                    type="text" 
                    required={formData.status !== 'Sales'}
                    value={formData.motStatus}
                    onChange={handleInputChange}
                    placeholder="e.g. MOT valid until 15 Jan 2026"
                    style={{ ...sharedInputStyle, ...motInputStyle }}
                  />
                  {motAlert && (
                    <div style={{ fontSize: '12px', color: motAlert.color, marginTop: '4px', fontWeight: 500 }}>
                      {motAlert.message}
                    </div>
                  )}
                </div>
                <div style={{ width: '100%', minWidth: 0 }}>
                  <label htmlFor="newTaxStatus">Tax Status</label>
                  <input 
                    id="newTaxStatus" 
                    name="taxStatus"
                    type="text" 
                    value={formData.taxStatus}
                    onChange={handleInputChange}
                    placeholder="e.g. Taxed - Due 1 Apr 2026"
                    style={sharedInputStyle}
                  />
                </div>
                <div style={{ width: '100%', minWidth: 0 }}>
                  <label htmlFor="newNextServiceDue">Next Service Due</label>
                  <input
                    id="newNextServiceDue"
                    name="nextServiceDue"
                    type="date"
                    value={formData.nextServiceDue || ''}
                    onChange={handleInputChange}
                    style={sharedInputStyle}
                  />
                </div>
                {formData.status !== 'Sales' && (
                <div style={{ width: '100%', minWidth: 0 }}>
                  <label htmlFor="newMileage">Mileage</label>
                  <input 
                    id="newMileage" 
                    name="mileage"
                    type="text" 
                    inputMode="numeric"
                    placeholder="e.g. 45000"
                    value={formData.mileage}
                    onChange={handleInputChange}
                    style={sharedInputStyle}
                  />
                </div>
                )}
              </div>
              );
            })()}
          </div>

          <div className="form-row">
            <label htmlFor="newVehicleName">Make & Model</label>
            <input 
              id="newVehicleName" 
              name="vehicleName"
              type="text" 
              required
              value={formData.vehicleName}
              onChange={handleInputChange}
            />
          </div>

          {formData.status !== 'Sales' && (
            <div className="form-row double">
            <div className="form-row">
              <label htmlFor="newTechnician">Technician</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <select
                    id="newTechnician"
                    name="technician"
                    value={showAddTechnician ? '' : formData.technician}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '__add_new__') {
                        setShowAddTechnician(true);
                      } else {
                        setShowAddTechnician(false);
                        handleInputChange(e);
                      }
                    }}
                    style={{ flex: 1, cursor: 'pointer' }}
                  >
                    <option value="">Select a technician...</option>
                    {technicians.map(name => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                    <option value="__add_new__">➕ Add new technician...</option>
                  </select>
                </div>
                {showAddTechnician && (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={newTechnicianName}
                      onChange={(e) => setNewTechnicianName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const name = newTechnicianName.trim();
                          if (name) {
                            const existingList = garageInfo?.technicians || [];
                            const existing = existingList.find(
                              (t) => (t || '').toLowerCase() === name.toLowerCase()
                            );
                            if (existing) {
                              setFormData(prev => ({ ...prev, technician: existing }));
                            } else {
                              const updated = [...existingList, name];
                              updateGarageInfo({ technicians: updated }).then(() => {
                                setFormData(prev => ({ ...prev, technician: name }));
                              });
                            }
                            setNewTechnicianName('');
                            setShowAddTechnician(false);
                          }
                        } else if (e.key === 'Escape') {
                          setShowAddTechnician(false);
                          setNewTechnicianName('');
                        }
                      }}
                      placeholder="Enter technician name"
                      autoFocus
                      style={{
                        flex: 1,
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: '1px solid var(--border)',
                        fontSize: '14px'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const name = newTechnicianName.trim();
                        if (!name) return;
                        const existingList = garageInfo?.technicians || [];
                        const existing = existingList.find(
                          (t) => (t || '').toLowerCase() === name.toLowerCase()
                        );
                        if (existing) {
                          setFormData(prev => ({ ...prev, technician: existing }));
                        } else {
                          const updated = [...existingList, name];
                          updateGarageInfo({ technicians: updated }).then(() => {
                            setFormData(prev => ({ ...prev, technician: name }));
                          });
                        }
                        setNewTechnicianName('');
                        setShowAddTechnician(false);
                      }}
                      style={{
                        padding: '10px 16px',
                        borderRadius: '8px',
                        border: '1px solid var(--border)',
                        background: 'var(--primary, #4f8df3)',
                        color: '#fff',
                        fontSize: '14px',
                        cursor: 'pointer',
                        fontWeight: 500
                      }}
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddTechnician(false);
                        setNewTechnicianName('');
                      }}
                      style={{
                        padding: '10px 16px',
                        borderRadius: '8px',
                        border: '1px solid var(--border)',
                        background: 'transparent',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="form-row">
              <label htmlFor="newCost">Estimated Cost</label>
              <input
                id="newCost"
                name="cost"
                type="number"
                min="0"
                step="0.01"
                value={formData.cost}
                onChange={handleInputChange}
              />
            </div>
            </div>
          )}

          {/* Cost fields table */}
          {formData.status === 'Sales' && (
          <div style={{
            background: 'var(--light-bg)',
            borderRadius: '12px',
            padding: '16px',
            marginTop: '16px',
            border: '1px solid var(--border)'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ 
                    padding: '8px 0',
                    width: '40%'
                  }}>
                    <label htmlFor="newCost" style={{ 
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: 'var(--text-dark)'
                    }}>
                      {formData.status === 'Sales' ? 'Purchase Price' : 'Estimated Cost'}
                    </label>
                    <input 
                      id="newCost" 
                      name="cost"
                      type="number" 
                      min="0" 
                      step="0.01"
                      value={formData.cost}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        borderRadius: '12px',
                        border: '1px solid var(--border)',
                        background: 'var(--white-bg)',
                        color: 'var(--text-dark)',
                        padding: '12px 16px',
                        fontSize: '15px'
                      }}
                    />
                  </td>
                  {formData.status === 'Sales' && (
                    <>
                      <td style={{ padding: '8px 0 8px 16px', width: '60%' }}>
                        <label htmlFor="newPrepCost" style={{ 
                          display: 'block',
                          marginBottom: '8px',
                          fontSize: '13px',
                          fontWeight: '500',
                          color: 'var(--text-dark)'
                        }}>
                          Prep Cost (£)
                        </label>
                        <input 
                          id="newPrepCost" 
                          name="prepCost"
                          type="number" 
                          min="0" 
                          step="0.01"
                          value={formData.prepCost}
                          onChange={handleInputChange}
                          style={{
                            width: '100%',
                            borderRadius: '12px',
                            border: '1px solid var(--border)',
                            background: 'var(--white-bg)',
                            color: 'var(--text-dark)',
                            padding: '12px 16px',
                            fontSize: '15px'
                          }}
                        />
                      </td>
                    </>
                  )}
                </tr>
                {formData.status === 'Sales' && (
                  <>
                    <tr>
                      <td style={{ padding: '8px 0', width: '40%' }}>
                        <label htmlFor="newWarranty" style={{ 
                          display: 'block',
                          marginBottom: '8px',
                          fontSize: '13px',
                          fontWeight: '500',
                          color: 'var(--text-dark)'
                        }}>
                          Warranty (£)
                        </label>
                        <input 
                          id="newWarranty" 
                          name="warranty"
                          type="number" 
                          min="0" 
                          step="0.01"
                          value={formData.warranty}
                          onChange={handleInputChange}
                          style={{
                            width: '100%',
                            borderRadius: '12px',
                            border: '1px solid var(--border)',
                            background: 'var(--white-bg)',
                            color: 'var(--text-dark)',
                            padding: '12px 16px',
                            fontSize: '15px'
                          }}
                        />
                      </td>
                      <td style={{ padding: '8px 0 8px 16px', width: '60%' }}>
                        <label htmlFor="newMisc" style={{ 
                          display: 'block',
                          marginBottom: '8px',
                          fontSize: '13px',
                          fontWeight: '500',
                          color: 'var(--text-dark)'
                        }}>
                          Misc (£)
                        </label>
                        <input 
                          id="newMisc" 
                          name="misc"
                          type="number" 
                          min="0" 
                          step="0.01"
                          value={formData.misc}
                          onChange={handleInputChange}
                          style={{
                            width: '100%',
                            borderRadius: '12px',
                            border: '1px solid var(--border)',
                            background: 'var(--white-bg)',
                            color: 'var(--text-dark)',
                            padding: '12px 16px',
                            fontSize: '15px'
                          }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="2" style={{ padding: '8px 0', width: '100%' }}>
                        <label htmlFor="newSalePrice" style={{ 
                          display: 'block',
                          marginBottom: '8px',
                          fontSize: '13px',
                          fontWeight: '500',
                          color: 'var(--text-dark)'
                        }}>
                          Sale Price (£)
                        </label>
                        <input 
                          id="newSalePrice" 
                          name="salePrice"
                          type="number" 
                          min="0" 
                          step="0.01"
                          value={formData.salePrice}
                          onChange={handleInputChange}
                          style={{
                            width: '100%',
                            borderRadius: '12px',
                            border: '1px solid var(--border)',
                            background: 'var(--white-bg)',
                            color: 'var(--text-dark)',
                            padding: '12px 16px',
                            fontSize: '15px'
                          }}
                        />
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
          )}

          {/* Sales-specific fields */}
          {formData.status === 'Sales' && (
            <>
              <div className="form-row">
                <div className="form-row">
                  <label htmlFor="newDealStage">Deal Stage</label>
                  <select 
                    id="newDealStage" 
                    name="dealStage"
                    required
                    value={formData.dealStage}
                    onChange={handleInputChange}
                  >
                    <option value="inStock">Vehicle in Stock</option>
                    <option value="inPrep">In Prep</option>
                    <option value="forSale">For Sale</option>
                    <option value="depositTaken">Deposit Taken</option>
                    <option value="sold">Sold</option>
                    <option value="collected">Collected</option>
                  </select>
                </div>
              </div>

              {/* Sales financial summary */}
              <div style={{
                background: 'var(--light-bg)',
                borderRadius: '12px',
                padding: '16px',
                marginTop: '16px',
                border: '1px solid var(--border)'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    <tr>
                      <td style={{ 
                        color: 'var(--text-muted)', 
                        fontSize: '13px',
                        padding: '8px 0',
                        borderBottom: '1px solid var(--border)'
                      }}>
                        Total Cost
                      </td>
                      <td style={{ 
                        fontWeight: '600', 
                        fontSize: '14px',
                        textAlign: 'right',
                        padding: '8px 0',
                        borderBottom: '1px solid var(--border)'
                      }}>
                        £{(parseFloat(formData.cost || 0) + parseFloat(formData.prepCost || 0) + parseFloat(formData.warranty || 0) + parseFloat(formData.misc || 0)).toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ 
                        fontWeight: '700', 
                        fontSize: '15px',
                        padding: '8px 0',
                        borderTop: '2px solid var(--border)',
                        marginTop: '4px'
                      }}>
                        Profit
                      </td>
                      <td style={{ 
                        fontWeight: '700',
                        fontSize: '15px',
                        textAlign: 'right',
                        padding: '8px 0',
                        borderTop: '2px solid var(--border)',
                        marginTop: '4px',
                        color: (parseFloat(formData.salePrice || 0) - (parseFloat(formData.cost || 0) + parseFloat(formData.prepCost || 0) + parseFloat(formData.warranty || 0) + parseFloat(formData.misc || 0))) >= 0 ? '#10B981' : '#EF4444'
                      }}>
                        £{(parseFloat(formData.salePrice || 0) - (parseFloat(formData.cost || 0) + parseFloat(formData.prepCost || 0) + parseFloat(formData.warranty || 0) + parseFloat(formData.misc || 0))).toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className="form-row double">
            <div className="form-row">
              <label htmlFor="newDropOff">Booked in</label>
              <input 
                id="newDropOff" 
                name="dropOffDate"
                type="date" 
                required
                value={formData.dropOffDate}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-row">
              <label htmlFor="newCollection">Collection Date</label>
              <input 
                id="newCollection" 
                name="collectionDate"
                type="date"
                value={formData.collectionDate}
                onChange={handleInputChange}
              />
            </div>
          </div>

          {/* Time Slot Selection - Only shown for MOT */}
          {formData.status === 'MOT' && (
            <div className="form-row">
              <label htmlFor="motSlotDuration">MOT slot length ({formData.motSlotDuration || DEFAULT_MOT_SLOT_DURATION} min booking)</label>
              <select
                id="motSlotDuration"
                name="motSlotDuration"
                value={formData.motSlotDuration || DEFAULT_MOT_SLOT_DURATION}
                onChange={(e) => {
                  const mins = Number(e.target.value);
                  setFormData((prev) => {
                    const next = { ...prev, motSlotDuration: mins };
                    if (prev.timeSlot && formData.dropOffDate) {
                      const slotKey = prev.timeSlot.includes('T')
                        ? prev.timeSlot
                        : `${formData.dropOffDate}T${prev.timeSlot}`;
                      if (isMotTimeSlotUnavailable(slotKey, formData.dropOffDate, mins, vehicles)) {
                        next.timeSlot = '';
                      }
                    }
                    return next;
                  });
                }}
                style={{ marginBottom: '14px', minHeight: '44px' }}
              >
                {MOT_SLOT_DURATIONS.map((mins) => (
                  <option key={mins} value={mins}>{mins} min</option>
                ))}
              </select>

              <label>Time Slot ({formData.motSlotDuration || DEFAULT_MOT_SLOT_DURATION} min booking)</label>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                gap: '10px',
                marginTop: '8px'
              }}>
                {generateMotTimeSlots(formData.dropOffDate).map((slot) => {
                  const isBooked = isMotTimeSlotUnavailable(
                    slot.key,
                    formData.dropOffDate,
                    formData.motSlotDuration || DEFAULT_MOT_SLOT_DURATION,
                    vehicles,
                  );
                  const isSelected = formData.timeSlot === slot.key;
                  
                  return (
                    <button
                      key={slot.key}
                      type="button"
                      onClick={() => {
                        if (!isBooked) {
                          setFormData(prev => ({ ...prev, timeSlot: slot.key }));
                        }
                      }}
                      disabled={isBooked}
                      style={{
                        padding: '12px 16px',
                        borderRadius: '12px',
                        border: isSelected 
                          ? '2px solid var(--primary)' 
                          : isBooked 
                            ? '1px solid var(--border)' 
                            : '1px solid rgba(79, 141, 243, 0.3)',
                        background: isSelected 
                          ? 'rgba(79, 141, 243, 0.1)' 
                          : isBooked 
                            ? 'var(--light-bg)' 
                            : 'var(--white-bg)',
                        color: isBooked 
                          ? 'var(--text-muted)' 
                          : 'var(--text-dark)',
                        fontSize: '14px',
                        fontWeight: isSelected ? '600' : '500',
                        cursor: isBooked ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        opacity: isBooked ? 0.5 : 1,
                        position: 'relative',
                        minHeight: '44px',
                      }}
                      onMouseEnter={(e) => {
                        if (!isBooked && !isSelected) {
                          e.target.style.background = 'rgba(79, 141, 243, 0.05)';
                          e.target.style.borderColor = 'var(--primary)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isBooked && !isSelected) {
                          e.target.style.background = 'var(--white-bg)';
                          e.target.style.borderColor = 'rgba(79, 141, 243, 0.3)';
                        }
                      }}
                    >
                      {slot.displayTime}
                      {isBooked && (
                        <span style={{
                          fontSize: '10px',
                          display: 'block',
                          marginTop: '4px',
                          color: 'var(--text-muted)'
                        }}>
                          Booked
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {!formData.timeSlot && (
                <div style={{
                  fontSize: '13px',
                  color: 'var(--text-muted)',
                  marginTop: '8px',
                  fontStyle: 'italic'
                }}>
                  Please select a time slot for this MOT booking
                </div>
              )}
            </div>
          )}

          {/* Tyres-specific fields - only shown for Tyres */}
          {formData.status === 'Tyres' && (
            <div className="form-row double" style={{ marginTop: '16px' }}>
              <div className="form-row">
                <label htmlFor="newTyreSize">Tyre Size</label>
                <input
                  id="newTyreSize"
                  name="tyreSize"
                  type="text"
                  value={formData.tyreSize}
                  onChange={handleInputChange}
                  placeholder="e.g. 205/55 R16"
                  style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', height: '44px' }}
                />
              </div>
              <div className="form-row">
                <label htmlFor="newTyreQuantity">Quantity</label>
                <input
                  id="newTyreQuantity"
                  name="tyreQuantity"
                  type="text"
                  value={formData.tyreQuantity}
                  onChange={handleInputChange}
                  placeholder="e.g. 4 or Front axle"
                  style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', height: '44px' }}
                />
              </div>
              <div className="form-row">
                <label htmlFor="newTyrePosition">Position</label>
                <input
                  id="newTyrePosition"
                  name="tyrePosition"
                  type="text"
                  value={formData.tyrePosition}
                  onChange={handleInputChange}
                  placeholder="e.g. Full set, FL, FR, RL, RR"
                  style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', height: '44px' }}
                />
              </div>
              <div className="form-row">
                <label htmlFor="newTyreServiceType">Service Type</label>
                <select
                  id="newTyreServiceType"
                  name="tyreServiceType"
                  value={formData.tyreServiceType}
                  onChange={handleInputChange}
                  style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', height: '44px' }}
                >
                  <option value="">Select...</option>
                  <option value="Supply only">Supply only</option>
                  <option value="Fit only">Fit only</option>
                  <option value="Supply and fit">Supply and fit</option>
                </select>
              </div>

              {/* Tyre Tread Depths */}
              <div className="form-row" style={{ gridColumn: '1 / -1', marginTop: '16px' }}>
                <label>Tyre Tread Depths</label>
                <div style={{ padding: '16px', background: 'var(--light-bg)', borderRadius: '12px', border: '1px solid var(--border)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '12px' }}>
                    {['FL', 'FR', 'RL', 'RR', 'SP'].map((pos) => (
                      <div key={pos}>
                        <label htmlFor={`newTread${pos}`} style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: 'var(--text-muted)' }}>{pos}</label>
                        <input id={`newTread${pos}`} name={`tread${pos}`} type="text" value={formData[`tread${pos}`] || ''} onChange={handleInputChange} placeholder="—" style={{ width: '100%', height: '40px', padding: '8px', borderRadius: '8px', border: '1px solid var(--border)' }} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="form-row">
            <label htmlFor="newSummary">{formData.status === 'Sales' ? 'Vehicle description' : 'Job summary'}</label>
            <textarea 
              id="newSummary" 
              name="summary"
              rows="2" 
              value={formData.summary}
              onChange={handleInputChange}
              onBlur={(e) => {
                const next = toSentenceCaps(e.target.value);
                if (next !== formData.summary) {
                  setFormData((prev) => ({ ...prev, summary: next }));
                }
              }}
            ></textarea>
          </div>

          {/* Vehicle Documentation Section - Only shown for Sales */}
          {formData.status === 'Sales' && (
            <>
              <div className="form-row double" style={{ marginTop: '16px' }}>
                <div className="form-row">
                  <label htmlFor="newV5C">Owners</label>
                  <input 
                    id="newV5C" 
                    name="v5c"
                    type="text"
                    value={formData.v5c}
                    onChange={handleInputChange}
                    placeholder="Enter number of owners"
                  />
                </div>
                <div className="form-row">
                  <label htmlFor="newKeys">Vehicle Mileage</label>
                  <input 
                    id="newKeys" 
                    name="keys"
                    type="text"
                    value={formData.keys}
                    onChange={handleInputChange}
                    placeholder="Enter vehicle mileage"
                  />
                </div>
              </div>

              <div className="form-row double">
                <div className="form-row">
                  <label htmlFor="newImport">Import</label>
                  <select 
                    id="newImport" 
                    name="import"
                    value={formData.import}
                    onChange={handleInputChange}
                  >
                    <option value="">Select...</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>
                <div className="form-row">
                  <label htmlFor="newServiceHistory">Service History</label>
                  <select 
                    id="newServiceHistory" 
                    name="serviceHistory"
                    value={formData.serviceHistory}
                    onChange={handleInputChange}
                  >
                    <option value="">Select...</option>
                    <option value="FSH">FSH</option>
                    <option value="PSH">PSH</option>
                    <option value="FMDH">FMDH</option>
                    <option value="None">None</option>
                  </select>
                </div>
              </div>
            </>
          )}
            </>
          )}

          {/* Customer Details Tab */}
          {activeTab === 1 && (
            <>
              <div className="form-row">
                <label htmlFor="newCustomerName">Customer Name</label>
                <input 
                  id="newCustomerName" 
                  name="customerName"
                  type="text"
                  value={formData.customerName}
                  onChange={handleInputChange}
                  placeholder="Enter customer full name"
                />
              </div>

              <div className="form-row double">
                <div className="form-row">
                  <label htmlFor="newContact">Contact Number</label>
                  <input 
                    id="newContact" 
                    name="contact"
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={formData.contact}
                    onChange={handleInputChange}
                    placeholder="Enter phone number"
                  />
                </div>
                <div className="form-row">
                  <label htmlFor="newEmail">Email Address</label>
                  <input 
                    id="newEmail" 
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={() => {
                      const v = (formData.email || '').trim();
                      setEmailError(v && !v.includes('@') ? 'Please enter a valid email address.' : '');
                    }}
                    placeholder="Enter email address"
                    aria-invalid={!!emailError}
                    aria-describedby={emailError ? 'email-error' : undefined}
                  />
                  {emailError && (
                    <span id="email-error" style={{ fontSize: '12px', color: '#EF4444', marginTop: '4px', display: 'block' }}>
                      {emailError}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <label htmlFor="newAddress">Address</label>
                <textarea 
                  id="newAddress" 
                  name="address"
                  rows="2"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Enter full address"
                ></textarea>
              </div>

              <div className="form-row double">
                <div className="form-row">
                  <label htmlFor="newCity">City/Town</label>
                  <input 
                    id="newCity" 
                    name="city"
                    type="text"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Enter city or town"
                  />
                </div>
                <div className="form-row">
                  <label htmlFor="newPostcode">Postcode</label>
                  <input 
                    id="newPostcode" 
                    name="postcode"
                    type="text"
                    value={formData.postcode}
                    onChange={handleInputChange}
                    placeholder="Enter postcode"
                  />
                </div>
              </div>

              <div className="form-row">
                <label htmlFor="newKeyCode">Key Number</label>
                <input 
                  id="newKeyCode" 
                  name="keyCode"
                  type="text"
                  value={formData.keyCode}
                  onChange={handleInputChange}
                  placeholder="Enter key number"
                  autoComplete="off"
                />
              </div>
            </>
          )}

          <div className="form-actions">
            <button 
              type="button" 
              className="secondary-btn" 
              id="cancelNewVehicle"
              onClick={handleClose}
            >
              Cancel
            </button>
            
            {activeTab === 0 && (
              <button 
                type="button" 
                className="primary-btn"
                onClick={() => setActiveTab(1)}
              >
                Next: Customer Details
              </button>
            )}
            
            {activeTab === 1 && (
              <>
                <button 
                  type="button" 
                  className="secondary-btn"
                  onClick={() => setActiveTab(0)}
                >
                  Back to Vehicle
                </button>
                <button type="submit" className="primary-btn">Add Vehicle</button>
              </>
            )}
          </div>
        </form>
      </div>
      
      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={handleUpgradeToPayment}
      />
    </div>
  );
};

export default BookingModal;
